import { computed, reactive, ref, type Ref } from 'vue'
import { smartBezier } from '../../../../../shared/flow-core/src/geometry'
import { NODE_HEIGHT, NODE_WIDTH, WORLD_ORIGIN } from '../model/constants'
import { inputPoint, inputPorts, outputPoint, outputPorts } from '../model/ports'
import type {
  FlowEdge,
  HandleKind,
  InteractionTool,
  NodeType,
  PaletteItem,
  Point,
} from '../model/types'
import type { FlowDocument } from './useFlowDocument'
import type { FlowEditing } from './useFlowEditing'
import type { FlowHistory } from './useFlowHistory'
import type { FlowSelection } from './useFlowSelection'
import type { FlowViewport } from './useFlowViewport'

type Deps = {
  document: FlowDocument
  selection: FlowSelection
  history: FlowHistory
  viewport: FlowViewport
  editing: FlowEditing
  interactionTool: Ref<InteractionTool>
  isSpacePanning: Ref<boolean>
  palette: Ref<PaletteItem[]>
}

type PortRef = { nodeId: string; port: string; kind: HandleKind }

const PALETTE_DRAG_TYPE = 'application/x-perlingo-flow-node'

/**
 * Mouse interactions on the canvas: moving nodes, panning, marquee selection, drawing
 * connections and dropping palette items. A gesture starts on mousedown and is tracked
 * with window listeners until mouseup.
 */
export function useCanvasPointer(deps: Deps) {
  const { document, selection, history, viewport, editing } = deps
  const { nodes, edges, nodeById } = document

  const connectionDrag = ref<{
    active: boolean
    fromNodeId: string
    fromPort: string
    fromKind: HandleKind
    currentX: number
    currentY: number
  } | null>(null)
  const dragState = ref<{
    anchorNodeId: string
    anchorOffsetX: number
    anchorOffsetY: number
    startPositions: Record<string, Point>
  } | null>(null)
  const panState = ref<{
    startClientX: number
    startClientY: number
    startOffsetX: number
    startOffsetY: number
  } | null>(null)
  const marquee = reactive({
    active: false,
    startClientX: 0,
    startClientY: 0,
    currentClientX: 0,
    currentClientY: 0,
  })
  const isCanvasDragOver = ref(false)

  const listen = () => {
    window.addEventListener('mousemove', handleMove)
    window.addEventListener('mouseup', stopPointer)
  }

  const startNodeDrag = (nodeId: string, event: MouseEvent) => {
    if (event.button !== 0) return
    const node = nodeById.value.get(nodeId)
    if (!node) return
    if (!selection.selectedNodeIds.value.includes(nodeId)) selection.selectNode(nodeId)

    const startPositions: Record<string, Point> = {}
    for (const id of selection.selectedNodeIds.value) {
      const selected = nodeById.value.get(id)
      if (selected) startPositions[id] = { x: selected.x, y: selected.y }
    }
    const world = viewport.toWorld(event)
    dragState.value = {
      anchorNodeId: nodeId,
      anchorOffsetX: world.x - node.x,
      anchorOffsetY: world.y - node.y,
      startPositions,
    }
    listen()
  }

  const startPan = (event: MouseEvent) => {
    panState.value = {
      startClientX: event.clientX,
      startClientY: event.clientY,
      startOffsetX: viewport.viewport.value.x,
      startOffsetY: viewport.viewport.value.y,
    }
    listen()
  }

  const startMarquee = (event: MouseEvent) => {
    marquee.active = true
    marquee.startClientX = marquee.currentClientX = event.clientX
    marquee.startClientY = marquee.currentClientY = event.clientY
    listen()
  }

  function handleMove(event: MouseEvent) {
    if (connectionDrag.value?.active) {
      const world = viewport.toWorld(event)
      connectionDrag.value.currentX = world.x
      connectionDrag.value.currentY = world.y
      return
    }

    const drag = dragState.value
    if (drag) {
      if (!nodeById.value.get(drag.anchorNodeId)) return
      const anchorStart = drag.startPositions[drag.anchorNodeId]
      if (!anchorStart) return
      const world = viewport.toWorld(event)
      const dx = world.x - drag.anchorOffsetX - anchorStart.x
      const dy = world.y - drag.anchorOffsetY - anchorStart.y
      for (const [id, start] of Object.entries(drag.startPositions)) {
        const node = nodeById.value.get(id)
        if (!node) continue
        node.x = start.x + dx
        node.y = start.y + dy
      }
      return
    }

    const pan = panState.value
    if (pan) {
      const dx = event.clientX - pan.startClientX
      const dy = event.clientY - pan.startClientY
      viewport.viewport.value.x = pan.startOffsetX + dx
      viewport.viewport.value.y = pan.startOffsetY + dy
      return
    }

    if (marquee.active) {
      marquee.currentClientX = event.clientX
      marquee.currentClientY = event.clientY
    }
  }

  /** Selects every node whose box intersects the marquee rectangle. */
  const applyMarqueeSelection = () => {
    const canvas = viewport.canvasEl.value
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const vp = viewport.viewport.value
    const toWorldX = (clientX: number) => (clientX - rect.left - vp.x) / vp.scale - WORLD_ORIGIN
    const toWorldY = (clientY: number) => (clientY - rect.top - vp.y) / vp.scale - WORLD_ORIGIN
    const left = toWorldX(Math.min(marquee.startClientX, marquee.currentClientX))
    const right = toWorldX(Math.max(marquee.startClientX, marquee.currentClientX))
    const top = toWorldY(Math.min(marquee.startClientY, marquee.currentClientY))
    const bottom = toWorldY(Math.max(marquee.startClientY, marquee.currentClientY))

    selection.selectNodes(
      nodes.value
        .filter(
          (node) =>
            !(
              node.x > right ||
              node.x + NODE_WIDTH < left ||
              node.y > bottom ||
              node.y + NODE_HEIGHT < top
            ),
        )
        .map((node) => node.id),
    )
  }

  /** Ends any gesture; a node drag that moved anything becomes one history entry. */
  function stopPointer() {
    const drag = dragState.value
    if (drag) {
      const moved = Object.entries(drag.startPositions).some(([id, start]) => {
        const node = nodeById.value.get(id)
        return (
          Boolean(node) &&
          (Math.abs(node!.x - start.x) > 0.01 || Math.abs(node!.y - start.y) > 0.01)
        )
      })
      if (moved) history.pushHistory('Move node')
    }
    if (marquee.active) {
      applyMarqueeSelection()
      marquee.active = false
    }
    dragState.value = null
    panState.value = null
    connectionDrag.value = null
    window.removeEventListener('mousemove', handleMove)
    window.removeEventListener('mouseup', stopPointer)
  }

  /** Background mousedown: middle button, Space/Alt or the pan tool pans; otherwise marquee. */
  const onCanvasMouseDown = (event: MouseEvent) => {
    if (event.button === 1) {
      event.preventDefault()
      startPan(event)
      return
    }
    if (event.button !== 0) return
    const target = event.target as HTMLElement | null
    if (
      target?.closest('.flow-node') ||
      target?.closest('.flow-handle') ||
      target?.closest('.flow-edge-hit')
    ) {
      return
    }
    selection.clearSelection()
    event.preventDefault()
    const panning =
      deps.isSpacePanning.value || event.altKey || deps.interactionTool.value === 'pan'
    if (!panning && (event.shiftKey || deps.interactionTool.value === 'marquee')) {
      startMarquee(event)
      return
    }
    startPan(event)
  }

  /** Node mousedown: Shift toggles selection, Space/Alt pans, otherwise drags the selection. */
  const onNodeMouseDown = (nodeId: string, event: MouseEvent) => {
    if (event.button !== 0) return
    const target = event.target as HTMLElement | null
    if (target?.closest('button,input,select,textarea,a')) return
    event.preventDefault()
    if (event.shiftKey) {
      const set = new Set(selection.selectedNodeIds.value)
      if (set.has(nodeId)) set.delete(nodeId)
      else set.add(nodeId)
      selection.selectNodes([...set])
      return
    }
    if (deps.isSpacePanning.value || event.altKey) {
      startPan(event)
      return
    }
    startNodeDrag(nodeId, event)
  }

  /** Normalises a handle pair into a valid output → input connection, or null. */
  const buildConnection = (start: PortRef, end: PortRef) => {
    if (start.kind === end.kind) return null
    const source = start.kind === 'output' ? start : end
    const target = source === start ? end : start
    const sourceNode = nodeById.value.get(source.nodeId)
    const targetNode = nodeById.value.get(target.nodeId)
    if (!sourceNode || !targetNode || source.nodeId === target.nodeId) return null
    if (!outputPorts(sourceNode).includes(source.port)) return null
    if (!inputPorts(targetNode).includes(target.port)) return null
    return {
      fromNodeId: source.nodeId,
      fromPort: source.port,
      toNodeId: target.nodeId,
      toPort: target.port,
    }
  }

  const startConnectionDrag = (
    nodeId: string,
    port: string,
    kind: HandleKind,
    event: MouseEvent,
  ) => {
    if (event.button !== 0) return
    event.preventDefault()
    event.stopPropagation()
    const world = viewport.toWorld(event)
    connectionDrag.value = {
      active: true,
      fromNodeId: nodeId,
      fromPort: port,
      fromKind: kind,
      currentX: world.x,
      currentY: world.y,
    }
    selection.selectNode(nodeId)
    listen()
  }

  /** Completes a connection; an input port accepts one edge, so it replaces any existing. */
  const finishConnectionDrag = (
    nodeId: string,
    port: string,
    kind: HandleKind,
    event: MouseEvent,
  ) => {
    const active = connectionDrag.value
    if (!active?.active) return
    event.preventDefault()
    event.stopPropagation()
    const created = buildConnection(
      { nodeId: active.fromNodeId, port: active.fromPort, kind: active.fromKind },
      { nodeId, port, kind },
    )
    if (created) {
      history.runMutation('Connect nodes', () => {
        edges.value = edges.value.filter(
          (edge) => !(edge.toNodeId === created.toNodeId && edge.toPort === created.toPort),
        )
        const next: FlowEdge = { id: document.nextEdgeId(), ...created }
        edges.value.push(next)
        selection.selectEdge(next.id)
      })
    }
    connectionDrag.value = null
  }

  /** Cancels an in-progress connection drag; returns whether one was active. */
  const cancelConnection = () => {
    if (!connectionDrag.value?.active) return false
    connectionDrag.value = null
    return true
  }

  const connectionPreviewPath = computed(() => {
    const active = connectionDrag.value
    if (!active?.active) return null
    const fromNode = nodeById.value.get(active.fromNodeId)
    if (!fromNode) return null
    const start =
      active.fromKind === 'output'
        ? outputPoint(fromNode, active.fromPort)
        : inputPoint(fromNode, active.fromPort)
    const end = { x: active.currentX + WORLD_ORIGIN, y: active.currentY + WORLD_ORIGIN }
    return active.fromKind === 'output' ? smartBezier(start, end) : smartBezier(end, start)
  })

  const marqueeStyle = computed(() => {
    const rect = viewport.canvasEl.value?.getBoundingClientRect()
    const left = Math.min(marquee.startClientX, marquee.currentClientX) - (rect?.left ?? 0)
    const top = Math.min(marquee.startClientY, marquee.currentClientY) - (rect?.top ?? 0)
    return {
      left: `${left}px`,
      top: `${top}px`,
      width: `${Math.abs(marquee.currentClientX - marquee.startClientX)}px`,
      height: `${Math.abs(marquee.currentClientY - marquee.startClientY)}px`,
    }
  })

  // Palette drag & drop
  const onPaletteDragStart = (item: PaletteItem, event: DragEvent) => {
    event.dataTransfer?.setData(PALETTE_DRAG_TYPE, item.id)
    event.dataTransfer?.setData('text/plain', item.id)
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'copy'
  }

  const onCanvasDragOver = (event: DragEvent) => {
    const types = event.dataTransfer?.types
    if (!types) return
    if (!types.includes(PALETTE_DRAG_TYPE) && !types.includes('text/plain')) return
    event.preventDefault()
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy'
    isCanvasDragOver.value = true
  }

  const onCanvasDragLeave = () => {
    isCanvasDragOver.value = false
  }

  const onCanvasDrop = (event: DragEvent) => {
    event.preventDefault()
    isCanvasDragOver.value = false
    const custom = event.dataTransfer?.getData(PALETTE_DRAG_TYPE) || ''
    const plain = event.dataTransfer?.getData('text/plain') || ''
    const rawId = (custom || plain).trim()
    if (!rawId) return
    const world = viewport.toWorld(event)
    const item = deps.palette.value.find((p) => p.id === rawId)
    if (item) {
      editing.addNodeFromPalette(item, world)
      return
    }
    // Fallback: treat the payload as a raw node type.
    if (['phase', 'group', 'condition', 'action'].includes(rawId)) {
      editing.addNodeAt(rawId as NodeType, world)
    }
  }

  return {
    connectionDrag,
    marquee,
    marqueeStyle,
    isCanvasDragOver,
    connectionPreviewPath,
    onCanvasMouseDown,
    onNodeMouseDown,
    startConnectionDrag,
    finishConnectionDrag,
    cancelConnection,
    stopPointer,
    onPaletteDragStart,
    onCanvasDragOver,
    onCanvasDragLeave,
    onCanvasDrop,
  }
}

export type CanvasPointer = ReturnType<typeof useCanvasPointer>
